import { Link } from 'react-router-dom';
import TeamBadge from '@/components/base/TeamBadge';
import { getTeam } from '@/utils/data';
import type { Match } from '@/types';

const statusLabel: Record<string, string> = { finished: '已完赛', live: '进行中', upcoming: '未开始' };

export default function MatchHeader({ match }: { match: Match }) {
  const home = getTeam(match.homeTeamId);
  const away = getTeam(match.awayTeamId);
  const hasScore = match.status !== 'upcoming';

  return (
    <section className="relative bg-secondary-950 overflow-hidden rounded-lg">
      <div className="absolute inset-0 bg-gradient-to-r from-secondary-950 via-secondary-900 to-secondary-950"></div>
      <div className="relative px-5 md:px-8 py-6 md:py-8">
        <div className="flex items-center justify-center gap-2 flex-wrap mb-6">
          <span className="text-xs px-2.5 py-1 rounded-full bg-background-50/10 text-background-100 border border-background-50/15">
            {match.leagueName}
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-background-50/10 text-background-100 border border-background-50/15">
            {match.stage}
          </span>
          <span
            className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${
              match.status === 'live' ? 'bg-primary-500 text-background-50' : 'bg-background-50/10 text-background-100 border border-background-50/15'
            }`}
          >
            {match.status === 'live' && <i className="ri-live-line animate-pulse-live"></i>}
            {statusLabel[match.status]}
          </span>
        </div>

        <div className="flex items-center justify-center gap-4 md:gap-12">
          <Link to={`/team/${home?.id}`} className="flex flex-col items-center gap-3 w-24 md:w-40 cursor-pointer group">
            <TeamBadge team={home} size="lg" />
            <span className="text-sm md:text-lg font-semibold text-background-50 text-center group-hover:text-primary-400 transition-colors leading-tight">
              {home?.name}
            </span>
          </Link>

          <div className="flex flex-col items-center">
            <div className="flex items-center gap-3 md:gap-5 text-background-50">
              <span className="font-heading font-extrabold text-5xl md:text-7xl tabular-nums">
                {hasScore ? match.homeScore : '-'}
              </span>
              <span className="font-heading text-3xl md:text-4xl text-background-50/40">:</span>
              <span className="font-heading font-extrabold text-5xl md:text-7xl tabular-nums">
                {hasScore ? match.awayScore : '-'}
              </span>
            </div>
            {match.status === 'live' && (
              <span className="mt-2 text-xs md:text-sm text-primary-400 font-medium">{match.progress}</span>
            )}
            {match.status === 'upcoming' && (
              <span className="mt-2 text-xs md:text-sm text-background-200">比赛尚未开始</span>
            )}
          </div>

          <Link to={`/team/${away?.id}`} className="flex flex-col items-center gap-3 w-24 md:w-40 cursor-pointer group">
            <TeamBadge team={away} size="lg" />
            <span className="text-sm md:text-lg font-semibold text-background-50 text-center group-hover:text-primary-400 transition-colors leading-tight">
              {away?.name}
            </span>
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs md:text-sm text-background-200">
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
            {match.date} {match.time}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
            {match.venue}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-fire-line"></i></span>
            热度 {match.hot}
          </span>
        </div>
      </div>
    </section>
  );
}